/** The inspector for a selected link: its ends, kind, label, reverse and delete. */
import { For, Show, createMemo, type JSX } from "solid-js";
import type { GraphChange } from "@protocol/GraphChange";
import type { Link } from "@protocol/Link";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { byId } from "../../shared/analysis";
import { toast } from "../../shared/toast";
import { NodeRef } from "../../shared/ui";
import { useOpenNode } from "./openNode";

/** Everything about one link. */
export function LinkPanel(props: { link: Link }): JSX.Element {
  const { graph, actions, view, kinds } = useApp();
  const open = useOpenNode();
  const nodes = createMemo(() => byId(graph.state.graph));
  /** The node the link starts from. */
  const from = () => nodes().get(props.link.from);
  /** The node the link points to. */
  const to = () => nodes().get(props.link.to);

  /** Applies a change and keeps the edited link selected. */
  const change = (patch: Parameters<typeof actions.updateLink>[1], message?: (l: Link) => string) => follow(actions.updateLink(props.link, patch), message);
  /** Keeps the link that `edit` changed selected, then says what it reads now. */
  const follow = async (edit: Promise<GraphChange[] | null>, message?: (l: Link) => string) => {
    const changes = await edit;
    const updated = changes?.find((c) => c.type === "link_updated");
    if (updated?.type === "link_updated") {
      view.selectLink(updated.after);
      view.showLinkKind(updated.after.kind);
      if (message) toast(message(updated.after));
    }
  };
  /** The link as a sentence ("Bus uses Logger."). */
  const sentence = (l: Link) => `${nodes().get(l.from)?.name ?? l.from} ${kinds.ofLink(l).verb} ${nodes().get(l.to)?.name ?? l.to}.`;

  return (
    <>
      <div class="ihead">
        <h2 class="ov">Link</h2>
        <p class="note">
          <b class="mono">{from()?.name}</b> {kinds.ofLink(props.link).verb} <b class="mono">{to()?.name}</b>
        </p>
      </div>
      <section class="grp">
        <h3>From</h3>
        <ul>
          <Show when={from()}>{(n) => <NodeRef node={n() as Node} onOpen={() => open(n().id)} />}</Show>
        </ul>
      </section>
      <section class="grp">
        <h3>Relation</h3>
        <div class="seg">
          <For each={kinds.links()}>
            {(k) => {
              const on = () => props.link.kind === k.id;
              return (
                <button type="button" class="seg-b" classList={{ on: on() }} aria-pressed={on()} data-set-kind={k.id} title={k.note || undefined} onClick={() => !on() && void follow(kinds.setLinkKind(props.link, k.id), sentence)}>
                  {k.label}
                </button>
              );
            }}
          </For>
        </div>
      </section>
      <section class="grp">
        <h3>To</h3>
        <ul>
          <Show when={to()}>{(n) => <NodeRef node={n() as Node} onOpen={() => open(n().id)} />}</Show>
        </ul>
      </section>
      <section class="grp">
        <h3>Label</h3>
        <input
          class="field"
          id="link-label"
          maxlength="60"
          autocomplete="off"
          value={props.link.label}
          placeholder="Optional, for example caches results"
          onChange={(e) => {
            const label = e.currentTarget.value.trim();
            if (label !== props.link.label) void change({ label });
          }}
        />
      </section>
      <div class="btnrow">
        <button type="button" class="btn" id="reverse-link" onClick={() => void change({ from: props.link.to, to: props.link.from }, sentence)}>
          Reverse direction
        </button>
        <button
          type="button"
          class="btn danger"
          id="delete-link"
          onClick={() =>
            void actions.removeLink(props.link).then((ok) => {
              if (!ok) return;
              view.selectLink(null);
              toast("Link removed. Press Ctrl+Z to undo.");
            })
          }
        >
          Delete link
        </button>
      </div>
      <p class="hint2">Drag the round handles at either end of the link to reconnect it. Press Delete to remove it and Ctrl+Z to undo.</p>
    </>
  );
}
